import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import type { TaskOccurrenceStatus } from "@dashboard/api/task";
import { useUpdateTaskOccurrenceStatus } from "@dashboard/hooks/useUpdateTaskOccurrenceStatus";
import { OCCURRENCE_STATUS_CONFIG } from "@dashboard/utils/occurrenceStatusConfig";
import {
    cn,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "common-frontend-components";
import type { ReactNode } from "react";

interface TaskStatusSelectProps {
    taskOccurrenceId: number;
    currentStatus: TaskOccurrenceStatus;
    disabled?: boolean;
}

const STATUS_ORDER: TaskOccurrenceStatus[] = ["OPEN", "IN_PROGRESS", "BLOCKED", "DONE"];

export function TaskStatus({
    status,
    className,
    children,
}: {
    status: TaskOccurrenceStatus;
    className?: string;
    children?: ReactNode;
}) {
    const { t } = useTranslation();
    const config = OCCURRENCE_STATUS_CONFIG[status];
    return (
        <span
            className={cn(
                "inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-xs font-medium transition-opacity",
                className,
            )}
            style={{ backgroundColor: config.bg, color: config.text }}
        >
            {t(`task.occurrenceStatus.${status}`)}
            {children}
        </span>
    );
}

export function TaskStatusSelect({
    taskOccurrenceId,
    currentStatus,
    disabled = false,
}: TaskStatusSelectProps) {
    const { t } = useTranslation();
    const { mutate, isPending } = useUpdateTaskOccurrenceStatus();

    const handleSelect = (status: TaskOccurrenceStatus) => {
        if (status !== currentStatus) {
            mutate({ id: taskOccurrenceId, status });
        }
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger disabled={disabled || isPending}>
                <TaskStatus
                    className={(isPending && "opacity-50 cursor-not-allowed") || "hover:opacity-80"}
                    status={currentStatus}
                >
                    <FontAwesomeIcon icon={faChevronDown} className="text-[10px]" />
                </TaskStatus>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
                {STATUS_ORDER.map((status) => {
                    const optionDot = OCCURRENCE_STATUS_CONFIG[status].dot;
                    return (
                        <DropdownMenuItem key={status} onSelect={() => handleSelect(status)}>
                            <span
                                className="inline-block w-2.5 h-2.5 rounded-full"
                                style={{ backgroundColor: optionDot }}
                            />
                            {t(`task.occurrenceStatus.${status}`)}
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
